// Google Identity Services ("Sign In With Google"), loaded on demand so we know when it is ready.

export interface GoogleInitializeConfig {
  client_id: string
  callback: (response: { credential: string }) => void
  /** Hosted-domain hint. UX only: the API is what enforces the domain. */
  hd?: string
  /** Lets prompt() issue a new token without a click when exactly one account is signed in. */
  auto_select?: boolean
}

export interface GooglePromptNotification {
  isNotDisplayed?: () => boolean
  isSkippedMoment?: () => boolean
}

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: GoogleInitializeConfig) => void
          renderButton: (parent: HTMLElement, options: Record<string, unknown>) => void
          prompt: (listener?: (notification: GooglePromptNotification) => void) => void
          disableAutoSelect: () => void
        }
      }
    }
  }
}

const SCRIPT_SRC = 'https://accounts.google.com/gsi/client'

let scriptPromise: Promise<void> | null = null

export function loadGoogleIdentityScript(): Promise<void> {
  scriptPromise ??= new Promise<void>((resolve, reject) => {
    if (document.querySelector(`script[src="${SCRIPT_SRC}"]`)) return resolve()
    const script = document.createElement('script')
    script.src = SCRIPT_SRC
    script.async = true
    script.onload = () => resolve()
    script.onerror = () => {
      scriptPromise = null
      reject(new Error('Failed to load Google Identity Services'))
    }
    document.head.appendChild(script)
  })
  return scriptPromise
}

export interface GoogleCredentialClaims {
  email: string
  name?: string
  /** Unix seconds. */
  exp: number
}

// Reads the payload without checking the signature, only for display and renewal timing.
// The API verifies the same token on every request.
export function decodeGoogleCredential(credential: string): GoogleCredentialClaims | null {
  const payload = credential.split('.')[1]
  if (!payload) return null
  try {
    const binary = atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
    const json = new TextDecoder().decode(Uint8Array.from(binary, (c) => c.charCodeAt(0)))
    return JSON.parse(json) as GoogleCredentialClaims
  } catch {
    return null
  }
}
